'use client';

import { Alert, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { extractErrorMessage } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/errorHandling';
import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { MainDialog } from '@/components/dialogs/mainDialog';
import { ParameterTable, UNMEASURED_TOOLTIP } from './parameterTable';
import { CheckingIndicator, stepFooterButtons, type StepDialogFadeProps } from './stepCheckControls';
import { deriveCdcSteps, useRdsCdcStatus } from '../useRdsCdcStatus';
const parameterGroupConsoleUrl = (region: string, parameterGroupName: string) => `https://${region}.console.aws.amazon.com/rds/home?region=${region}#parameter-group-details:ids=${parameterGroupName}`;
export const DatabaseParametersDialog = ({
  resourceId,
  region,
  onBack,
  onClose,
  ...fadeProps
}: {
  resourceId: string;
  region: string;
  onBack: () => void;
  onClose: () => void;
} & StepDialogFadeProps) => {
  const [isChecking, setIsChecking] = useState(false);
  const [checkError, setCheckError] = useState<string | null>(null);
  const {
    isLoading,
    refresh,
    parameterChecks,
    isParametersStepDone
  } = useRdsCdcStatus(resourceId);
  const parameterGroupName = parameterChecks[0]?.parameterGroupName ?? '';
  const unmeasuredChecks = parameterChecks.filter(param => param.unmeasured);
  const runCheck = async (continueWhenDone: boolean) => {
    setIsChecking(true);
    setCheckError(null);
    try {
      const latest = await refresh();
      // deriveCdcSteps rather than an inline every(): an empty parameterChecks list is vacuously
      // "all ok" but means the server reported nothing, and the hub would still show the step failed.
      if (continueWhenDone && deriveCdcSteps(latest).isParametersStepDone) {
        onBack();
      }
    } catch (refreshError) {
      setCheckError(await extractErrorMessage(refreshError));
    } finally {
      setIsChecking(false);
    }
  };
  return <MainDialog isLoading={isLoading} scrollableBody {...fadeProps} header={{
    title: 'Set database parameters',
    subtitle: "These parameters must be set on the instance's parameter group before Eon can read changes.",
    onBack,
    onClose
  }} footer={{
    ...stepFooterButtons({
      isDone: isParametersStepDone,
      isBusy: isChecking,
      // Re-checking the parameter group cannot settle a limit whose sufficiency depends on the
      // running server, so the step stays open to read but refuses to be completed.
      blockedReason: unmeasuredChecks.length > 0 ? UNMEASURED_TOOLTIP : undefined,
      testIdPrefix: 'rds-cdc-parameters',
      onContinue: onBack,
      onCheckAndContinue: () => void runCheck(true),
      onVerifyAgain: () => void runCheck(false)
    }),
    children: !!region && !!parameterGroupName && <ButtonWithIcon variant='text' icon='material-symbols-open-in-new' iconPosition='right' text='Open parameter group in AWS Console' onClick={() => window.open(parameterGroupConsoleUrl(region, parameterGroupName), '_blank', 'noopener')} />
  }}>
      <Stack sx={{
      gap: '16px',
      paddingX: '40px'
    }}>
        {!isChecking && parameterChecks.length === 0 && <Alert severity='error'>
            Eon couldn&apos;t read the instance&apos;s parameter group, so there
            is nothing to check yet. Refresh and try again, or contact support
            if it keeps happening.
          </Alert>}
        {parameterChecks.length > 0 && <>
            {!!parameterGroupName && <Stack direction='row' sx={{
          gap: '8px',
          alignItems: 'baseline'
        }}>
                <Typography variant='subtitle1Strong'>
                  Parameter group:
                </Typography>
                <Typography variant='body1'>
                  <code>{parameterGroupName}</code>
                </Typography>
              </Stack>}
            <ParameterTable params={parameterChecks} />
          </>}
        {isChecking && <CheckingIndicator text='Checking the parameter group. This might take a few seconds.' />}
        {!isChecking && isParametersStepDone && <Alert severity='success'>All required parameters are set.</Alert>}
        {!isChecking && !!checkError && <Alert severity='error'>{checkError}</Alert>}
      </Stack>
    </MainDialog>;
};